import {
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import "./style.css";
import { useDispatch } from "react-redux";

const ValuesAndRangesComponent = ({
  rangePairs,
  values,
  setRangePairs,
  setValues,
  options,
}) => {
  const dispatch = useDispatch();
  // range

  const [startValue, setStartValue] = useState("");
  const [endValue, setEndValue] = useState("");
  const [floatWarning, setFloatWarning] = useState("");

  const handleStartChange = (event) => {
    setStartValue(event.target.value);
  };

  const handleEndChange = (event) => {
    setEndValue(event.target.value);
  };

  // Helper function to check if a value is a flag or nodata
  const isRestrictedValue = (numValue, inputStr) => {
    // Get flags and nodata from options
    const flagValues = options?.flags || [];
    const nodataValue = options?.nodata;

    // Check if value is a flag
    if (flagValues.length > 0) {
      // Check both numeric and string representations
      const isFlag = flagValues.some(flag => {
        if (flag === "nan" && inputStr.toLowerCase() === "nan") return true;
        if (!isNaN(Number(flag)) && Number(flag) === numValue) return true;
        return false;
      });
      if (isFlag) {
        toast.error("Cannot use flag values for masking");
        return true;
      }
    }

    // Check if value is nodata
    if (nodataValue !== undefined && nodataValue !== null) {
      const isNoData =
        (nodataValue === "nan" && inputStr.toLowerCase() === "nan") ||
        (!isNaN(Number(nodataValue)) && Number(nodataValue) === numValue);

      if (isNoData) {
        toast.error("Cannot use 'no data' value for masking");
        return true;
      }
    }

    return false;
  };

  // Helper function to check if a range contains restricted values
  const rangeContainsRestrictedValue = (start, end) => {
    const flagValues = options?.flags || [];
    const nodataValue = options?.nodata;

    // Check flags
    for (const flag of flagValues) {
      if (flag !== "nan" && !isNaN(Number(flag))) {
        const flagNum = Number(flag);
        if (flagNum >= start && flagNum <= end) {
          toast.error(`Range contains flag value: ${flag}`);
          return true;
        }
      }
    }

    // Check nodata
    if (nodataValue !== undefined && nodataValue !== null && nodataValue !== "nan") {
      const nodataNum = Number(nodataValue);
      if (!isNaN(nodataNum) && nodataNum >= start && nodataNum <= end) {
        toast.error(`Range contains 'no data' value: ${nodataValue}`);
        return true;
      }
    }

    return false;
  };

  const handleAddRange = () => {
    if (!startValue || !endValue) {
      toast.error("Please enter both start and end values.");
      return;
    }
    const start = parseFloat(startValue.trim());
    const end = parseFloat(endValue.trim());
    if (isNaN(start) || isNaN(end)) {
      toast.error("Please enter valid numeric values for start and end.");
      return;
    }

    // Check if range contains restricted values
    if (rangeContainsRestrictedValue(Math.min(start, end), Math.max(start, end))) {
      return;
    }

    // Warning for float values in range
    if (!Number.isInteger(start) || !Number.isInteger(end)) {
      toast.warning(
        "Note: Using float values for masking range. Ensure this matches your data precision."
      );
    }

    dispatch(setRangePairs([...rangePairs, [start, end]]));
    setStartValue("");
    setEndValue("");
  };

  //   multiple selection

  const [inputValue, setInputValue] = useState("");

  const handleInputChangeMultiple = (event) => {
    setInputValue(event.target.value);
    setFloatWarning(""); // Clear warning when typing
  };

  const handleAddValue = () => {
    if (inputValue.trim() !== "") {
      const numValue = parseFloat(inputValue.trim());

      if (isNaN(numValue)) {
        toast.error("Please enter a valid numeric value.");
        return;
      }

      // Check if value is a flag or nodata
      if (isRestrictedValue(numValue, inputValue.trim())) {
        return;
      }

      if (!Number.isInteger(numValue)) {
        setFloatWarning("Note: Float values must match your data precision exactly.");
      }

      // Check for duplicate values
      if (values.includes(numValue)) {
        toast.error("Value already added");
        return;
      }

      dispatch(setValues([...values, numValue]));
      setInputValue("");
    }
  };

  const handleDeleteRange = (index) => {
    // Create a new array without the range at the specified index
    const updatedRangePairs = rangePairs.filter((_, i) => i !== index);
    // Update the state with the new array
    dispatch(setRangePairs(updatedRangePairs));
  };

  const handleDeleteValues = (index) => {
    const updatedValues = [...values]; // Create a copy of the current values array
    updatedValues.splice(index, 1); // Remove the value at the specified index
    dispatch(setValues(updatedValues)); // Update the state with the new values array
  };

  // Handle Enter key press for adding values
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddValue();
    }
  };

  // Handle Enter key press for adding range
  const handleRangeKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddRange();
    }
  };

  return (
    <div className="masking-range-values-container">
      <div>
        <Typography fontWeight={500} fontFamily={"unset"} fontSize={22}>
          Values to mask by
        </Typography>

        {/* Info about flags and nodata */}
        {(options?.flags?.length > 0 || options?.nodata !== undefined) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', marginBottom: '10px', fontStyle: 'italic' }}
          >
            Note: Flag values {options?.flags?.length > 0 ? `(${options.flags.join(', ')})` : ''}
            {options?.nodata !== undefined ? ` and No Data value (${options.nodata})` : ''}
            {' '}cannot be used for masking.
          </Typography>
        )}

        {/* Range of values */}
        <Typography textAlign={"left"} fontWeight={500} fontSize={18}>
          Range Value
        </Typography>
        <div>
          <Grid container spacing={2}>
            <Grid item>
              <TextField
                size="small"
                label="Start"
                value={startValue}
                onChange={handleStartChange}
                onKeyPress={handleRangeKeyPress}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <TextField
                size="small"
                label="End"
                value={endValue}
                onChange={handleEndChange}
                onKeyPress={handleRangeKeyPress}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Button
                size="small"
                onClick={handleAddRange}
                variant="contained"
                color="primary"
              >
                Add Range
              </Button>
            </Grid>
            <br />
          </Grid>
          <Grid marginTop={"1em"}>
            <Grid
              flexWrap={"wrap"}
              gap={"1em"}
              display={"flex"}
              flexDirection={"row"}
              width={"100%"}
              marginTop={"1em"}
            >
              {rangePairs.map((range, index) => (
                <div style={{ display: "flex" }} key={`range-${index}`}>
                  <Typography className="selected">
                    Range {index + 1}: {range[0]} - {range[1]}
                  </Typography>
                  <div
                    className="delete-icon-mask"
                    onClick={() => handleDeleteRange(index)}
                  >
                    <DeleteIcon
                      sx={{
                        cursor: "pointer",
                      }}
                      color="error"
                    />
                  </div>
                  <br />
                </div>
              ))}
            </Grid>
          </Grid>
        </div>
        <br />
        {/* multiple selection */}
        <Typography textAlign={"left"} fontWeight={500} fontSize={18}>
          Single Value
        </Typography>
        <div>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                size="small"
                label="Enter Value"
                value={inputValue}
                onChange={handleInputChangeMultiple}
                onKeyPress={handleKeyPress}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Button
                size="small"
                onClick={handleAddValue}
                variant="contained"
                color="primary"
              >
                Add
              </Button>
              {floatWarning && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    marginTop: '8px',
                    color: '#ed6c02',
                    fontStyle: 'italic'
                  }}
                >
                  ⚠️ {floatWarning}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid marginTop={"1em"}>
            <Grid
              flexWrap={"wrap"}
              gap={"1em"}
              display={"flex"}
              flexDirection={"row"}
              width={"100%"}
              marginTop={"1em"}
            >
              {values.map((value, index) => (
                <div style={{ display: "flex" }} key={`value-${index}`}>
                  <Typography className="selected">
                    Value {index + 1}: {value}
                  </Typography>
                  <div
                    className="delete-icon-mask"
                    onClick={() => handleDeleteValues(index)}
                  >
                    <DeleteIcon
                      sx={{
                        cursor: "pointer",
                      }}
                      color="error"
                    />
                  </div>
                  <br />
                </div>
              ))}
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default ValuesAndRangesComponent;
